import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTheme } from 'styled-components'

import type { LotCarbonEmissions } from 'api'
import { GreyCardWithTitle } from 'components'
import { MetricValue } from 'features/MainRouter/features/DasboardRouter/features/components'

import {
  CarbonEmissionsTooltip,
  ChartContainer,
  ChartHeader,
  ChartSection,
  ChartTitle,
  ChartUnit,
  EmptyState,
  EmptyStateText,
  MetricContainer,
  MetricLabel,
  MetricsContainer,
} from './components'

interface Props {
  emissions?: LotCarbonEmissions['lot']['carbonEmissions'] | null
}

export const LotCarbonEmissionsCard: React.FC<Props> = ({ emissions }) => {
  const { colors } = useTheme()
  const { t } = useTranslation('lot-dashboard')
  const { t: commonT } = useTranslation('common')

  const formatEmission = (value?: number | null) => {
    if (value === null || value === undefined) return undefined

    return commonT('systemsFormat.localizeNumber', { val: Math.round(value) })
  }

  const formatTick = (value: number) => commonT('systemsFormat.localizeNumber', { val: value })
  const chartData = [...(emissions?.dailyMethaneEmissions ?? [])].sort(
    (firstValue, secondValue) => firstValue.daysSinceEmergence - secondValue.daysSinceEmergence,
  )
  const unit = commonT('systems.carbonDioxideEquivalentKilograms')

  return (
    <GreyCardWithTitle title={t('carbonEmissionsCard.title')}>
      <MetricsContainer>
        <MetricContainer>
          <MetricLabel>{t('carbonEmissionsCard.methane')}</MetricLabel>
          <MetricValue
            containerWidth={168}
            value={formatEmission(emissions?.methaneCo2eKg)}
            unit={unit}
            valueWidth={70}
            variant="outlined"
          />
        </MetricContainer>
        <MetricContainer>
          <MetricLabel>{t('carbonEmissionsCard.nitrousOxide')}</MetricLabel>
          <MetricValue
            containerWidth={168}
            value={formatEmission(emissions?.nitrousOxideCo2eKg)}
            unit={unit}
            valueWidth={70}
            variant="outlined"
          />
        </MetricContainer>
      </MetricsContainer>

      <ChartSection>
        <ChartHeader>
          <ChartTitle>{t('carbonEmissionsCard.chart.title')}</ChartTitle>
          <ChartUnit>{t('carbonEmissionsCard.chart.unit')}</ChartUnit>
        </ChartHeader>

        <ChartContainer>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 4,
                  right: 20,
                  left: 12,
                  bottom: 12,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="daysSinceEmergence"
                  label={{
                    value: t('carbonEmissionsCard.chart.xAxis'),
                    position: 'insideBottomRight',
                    offset: -8,
                  }}
                  tickFormatter={formatTick}
                />
                <YAxis tickFormatter={formatTick} />
                <Tooltip
                  content={({ active, payload }) => (
                    <CarbonEmissionsTooltip active={active} payload={payload} />
                  )}
                />
                <Bar dataKey="methaneCo2eKg" fill={colors.blue} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState>
              <EmptyStateText>{t('carbonEmissionsCard.chart.noData')}</EmptyStateText>
            </EmptyState>
          )}
        </ChartContainer>
      </ChartSection>
    </GreyCardWithTitle>
  )
}
